// Le drill de réaction « en séquence » en plein écran (D-161, choix 7 ; maquette d5 de D-162). La frise d'un tour
// (son, attente, signal) se répète ; le temps est calé sur l'horloge comme le fractionné : le plan (domain/drills.ts,
// seqPlan) dit à tout instant la brique en cours, juste après une mise en veille (un son en retard n'est pas rejoué).
// Le même lecteur sert l'étape « Signaux » d'un circuit qui reprend une séquence. En haut : le drill, « Tour 7 / 20 · le
// signe » ; en bas, les briques du tour (faites, en cours, à venir). Un signe s'affiche en grand sur une carte claire,
// le nom du système après 1 s (drill-view.ts, signCueHtml).
import {
  COUNTDOWN_MS, expandPool, fmtClock, pickCue, seqPlan, seqTours,
  type Cue, type SeqEvent, type SeqPlan, type SeqSound, type Sequence, type Stimuli,
} from '../domain/drills';
import { beep, playEndSound, speak, tick, unlockAudio } from './drill-engine';
import { cueHtml } from './drill-view';
import { $, html, mount, type Html } from './dom';

export interface SeqCtx { roster: { id: string; name: string }[]; images: Map<string, string>; signNames: Map<string, string> }

/** Le son d'une brique Son (synthétisé, comme tous les sons d'Arceau ; « Go » : la voix du téléphone). */
export function playSeqSound(s: SeqSound): void {
  if (s === 'bip') { beep(880); return; }
  if (s === 'go') { speak('Go !'); return; }
  if (s === 'bip-long') {
    try {
      const a = unlockAudio(); if (!a) return;
      const o = a.createOscillator(), g = a.createGain(), t = a.currentTime;
      o.type = 'square'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.28, t + 0.01); g.gain.setValueAtTime(0.28, t + 0.4); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.48);
      o.connect(g); g.connect(a.destination); o.start(t); o.stop(t + 0.5);
    } catch { /* sans son */ }
    return;
  }
  playEndSound(s);
}

/** « la flèche », « le chiffre », « le signe » : ce que montre une brique Signal. */
export function signalWord(st: Stimuli): string {
  const fams = [st.signs && 'le signe', st.arrows?.length && 'la flèche', st.colors?.length && 'la couleur', st.numbers && 'le chiffre',
    st.letters && 'la lettre', st.shapes?.length && 'la forme', st.images?.length && 'l’image', st.players && 'le prénom'].filter(Boolean) as string[];
  return fams.length === 1 ? fams[0]! : 'le signal';
}

export interface SeqPlayer {
  plan: SeqPlan;
  /** Affiche la brique à `e` ms du départ de la séquence ; faux : c'est fini. */
  tick(e: number): boolean;
  /** Après une pause : la brique en cours est réaffichée (sans rejouer son son). */
  reset(): void;
}
/** Le lecteur d'une séquence dans `cueEl`. `onEnter` : une nouvelle brique commence. */
export function seqPlayer(cueEl: HTMLElement, q: Sequence, ctx: SeqCtx, onEnter?: (ev: SeqEvent, plan: SeqPlan) => void): SeqPlayer {
  const plan = seqPlan(q);
  const pools = q.steps.map(s => (s.kind === 'signal' ? expandPool(s.stimuli, { roster: ctx.roster, images: [...ctx.images.keys()], signNames: ctx.signNames }) : []));
  const prev: (Cue | null)[] = q.steps.map(() => null);
  let idx = -1, lastN = 0;
  const enter = (ev: SeqEvent, e: number) => {
    if (ev.kind === 'decompte') { onEnter?.(ev, plan); return; }   // le 3, 2, 1 s'affiche dans tick
    const s = q.steps[ev.step]!;
    const fresh = e - ev.start < 600;   // après une mise en veille, un son en retard n'est pas rejoué
    if (s.kind === 'signal') {
      const c = pickCue(pools[ev.step]!, Math.random, prev[ev.step]);
      prev[ev.step] = c;
      mount(cueEl, cueHtml(c, ctx.images));
      if (s.beep && fresh) beep();
    } else {
      mount(cueEl, s.kind === 'son' ? html`<span class="dq-note" aria-hidden="true">♪</span>` : html``);
      if (s.kind === 'son' && fresh) playSeqSound(s.sound);
    }
    onEnter?.(ev, plan);
  };
  return {
    plan,
    tick(e) {
      const r = plan.at(e);
      if (!r) return false;
      if (r.index !== idx) { idx = r.index; lastN = 0; enter(r.ev, e); }
      if (r.ev.kind === 'decompte') {
        // Le décompte avant le tour : 3, 2, 1 (le son seulement s'il n'est pas en retard).
        const n = Math.max(1, Math.ceil((r.ev.end - e) / 1000));
        if (n !== lastN) { const late = e - (r.ev.end - n * 1000) > 600; lastN = n; if (!late) tick(n === 1 ? 990 : 660); mount(cueEl, html`<span class="dq-count">${n}</span>`); }
      }
      return true;
    },
    reset() { idx = -1; lastN = 0; },
  };
}

/** Les briques du tour en cours, en bas de l'écran (faites, en cours, à venir). */
export const stepsBar = (evs: SeqEvent[], cur: SeqEvent): Html =>
  html`${evs.map(x => html`<i class="${x.slot < cur.slot ? 'done' : x.slot === cur.slot ? 'on' : ''}" style="flex:${Math.max(1, x.end - x.start)}"></i>`)}`;

interface Kit {
  frame: (cls: string, title: string, opts: { onClose?: () => void }, cleanup: () => void) => { host: HTMLElement; close: () => void };
  stopButton: (host: HTMLElement, pauseFor: (on: boolean) => void, close: () => void, hint?: (t: string | null) => void) => void;
  endScreen: (icon: string, title: string, line: string) => Html;
}

/** Le drill « en séquence » en plein écran. */
export function runSequence(title: string, q: Sequence, ctx: SeqCtx, opts: { onClose?: () => void }, kit: Kit): void {
  let driver = 0;
  const f = kit.frame('dr-run dq-run', title, opts, () => clearInterval(driver));
  const { host } = f;
  const tours = q.repeat.mode === 'tours' ? seqTours(q) : null;
  const off = q.countdown && !q.countdown_each ? COUNTDOWN_MS : 0;
  let epoch = 0, pausedAcc = 0, pausedAt: number | null = null, done = false, lastCount = 0, player: SeqPlayer | null = null, toursDone = 0;
  const elapsed = () => Math.max(0, Date.now() - epoch - pausedAcc - (pausedAt !== null ? Date.now() - pausedAt : 0));
  mount(host, html`
    <span class="bar two" data-bar><span>${title}</span><span data-where></span></span><button type="button" class="x" data-x aria-label="Arrêter le drill">×</button>
    <div class="dr-cue" data-cue aria-live="polite"></div>
    <span class="hint" data-hint>Écran maintenu allumé · tourne le téléphone si tu veux</span>
    <div class="dq-steps" data-steps aria-hidden="true"></div>`);
  const cueEl = $(host, '[data-cue]')!, where = $(host, '[data-where]')!, stepsEl = $(host, '[data-steps]')!, hintEl = $(host, '[data-hint]')!;
  let hintT = window.setTimeout(() => hintEl.classList.add('out'), 4000);
  const hint = (t: string | null) => { clearTimeout(hintT); if (t) { hintEl.textContent = t; hintEl.classList.remove('out'); } else { hintEl.classList.add('out'); hintT = 0; } };
  let curEv: SeqEvent | null = null;
  const label = (ev: SeqEvent) => {
    const s = q.steps[ev.step];
    const what = !s ? 'prêts ?' : s.kind === 'son' ? 'le son' : s.kind === 'attente' ? 'attente' : signalWord(s.stimuli);
    const left = q.repeat.mode === 'minutes' ? ` · ${fmtClock(q.repeat.value * 60_000 - Math.max(0, elapsed() - off))} restantes` : '';
    return `Tour ${ev.tour}${tours ? ` / ${tours}` : ''} · ${what}${left}`;
  };
  const onEnter = (ev: SeqEvent, plan: SeqPlan) => {
    curEv = ev; toursDone = Math.max(toursDone, ev.tour - 1);
    where.textContent = label(ev);
    mount(stepsEl, stepsBar(plan.tour(ev.tour).filter(x => x.kind !== 'decompte'), ev));
  };
  const finish = () => {
    done = true; clearInterval(driver);
    toursDone = tours ?? Math.max(toursDone, curEv?.tour ?? 0);
    mount(stepsEl, html``); where.textContent = '';
    $(host, '[data-x]')!.hidden = true;
    mount(cueEl, kit.endScreen('✓', 'Terminé', `${toursDone} tour${toursDone > 1 ? 's' : ''}`));
    $(cueEl, '[data-close]')!.addEventListener('click', f.close);
    $(cueEl, '[data-again]')!.addEventListener('click', () => { $(host, '[data-x]')!.hidden = false; start(); });
  };
  const tickFn = () => {
    if (done || pausedAt !== null || !player) return;
    const e = elapsed();
    if (e < off) {
      // Le décompte avant le départ : 3, 2, 1.
      const n = Math.ceil((off - e) / 1000);
      if (n !== lastCount) { lastCount = n; tick(n === 1 ? 990 : 660); mount(cueEl, html`<span class="dq-count">${n}</span>`); where.textContent = 'Prêts ?'; }
      return;
    }
    if (!player.tick(e - off)) finish();
    else if (q.repeat.mode === 'minutes' && curEv) where.textContent = label(curEv);
  };
  kit.stopButton(host, on => {
    if (done) return;
    if (on && pausedAt === null) { pausedAt = Date.now(); mount(cueEl, html``); }
    else if (!on && pausedAt !== null) { pausedAcc += Date.now() - pausedAt; pausedAt = null; player?.reset(); lastCount = 0; tickFn(); }
  }, f.close, hint);
  const start = () => {
    done = false; epoch = Date.now(); pausedAcc = 0; pausedAt = null; lastCount = 0; toursDone = 0; curEv = null;
    player = seqPlayer(cueEl, q, ctx, onEnter);
    clearInterval(driver);
    driver = window.setInterval(tickFn, 40);
    tickFn();
  };
  start();
}
